import React, { useState } from 'react';
import { apiFetch } from '../../api';
import { useDarkMode } from '../../contexts/DarkModeContext';

interface DmsDocument {
  id: string;
  title: string;
  document_number: string | null;
  version: string;
  description: string | null;
  file_name: string;
  mime_type: string;
  valid_from: string;
  valid_until: string | null;
}

interface DmsAcknowledgeModalProps {
  document: DmsDocument;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DmsAcknowledgeModal({
  document,
  onClose,
  onSuccess,
}: DmsAcknowledgeModalProps): JSX.Element {
  const [step, setStep] = useState<1 | 2>(1);
  const [step1Checked, setStep1Checked] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDark = (() => {
    try {
      const dm = useDarkMode();
      return dm.isDarkMode;
    } catch {
      return document.documentElement.classList.contains('dark');
    }
  })();

  const theme = {
    cardBg: isDark ? '#1e293b' : '#ffffff',
    innerBg: isDark ? '#0f172a' : '#f8fafc',
    border: isDark ? '#334155' : '#e2e8f0',
    textMain: isDark ? '#f8fafc' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    textSub: isDark ? '#cbd5e1' : '#334155',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    inputBorder: isDark ? '#475569' : '#cbd5e1',
    accent: isDark ? '#38bdf8' : '#0284c7',
  };

  async function handleFinalSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!password) {
      setError('Bitte geben Sie Ihr Anmeldekennwort ein.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiFetch(`/dms/documents/${document.id}/acknowledge`, {
        method: 'POST',
        body: JSON.stringify({
          step1_confirmed: true,
          password: password,
        }),
      });

      onSuccess();
    } catch (err: any) {
      setError(err?.message || 'Fehler beim Abzeichnen. Bitte prüfen Sie Ihr Kennwort.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadDocument(e: React.MouseEvent, docId: string) {
    e.preventDefault();
    const token = localStorage.getItem('token') || '';
    try {
      const res = await fetch(`/api/dms/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error('Download fehlgeschlagen');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      window.open(`/api/dms/documents/${docId}/download?token=${encodeURIComponent(token)}`, '_blank');
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          backgroundColor: theme.cardBg,
          color: theme.textMain,
          borderRadius: '12px',
          width: '100%',
          maxWidth: '650px',
          boxShadow: isDark
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.6)'
            : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${theme.border}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            backgroundColor: theme.innerBg,
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: theme.accent }}>
              Kenntnisnahme & Abzeichnung
            </h3>
            <div style={{ fontSize: '0.875rem', color: theme.textMuted, marginTop: '4px' }}>
              {document.document_number ? `[${document.document_number}] ` : ''}
              {document.title} (Version {document.version})
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.textMuted,
              fontSize: '1.5rem',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Step Indicator */}
        <div
          style={{
            display: 'flex',
            padding: '12px 24px',
            backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
            borderBottom: `1px solid ${theme.border}`,
            gap: '12px',
          }}
        >
          <div
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: step === 1 ? '#0284c7' : isDark ? '#334155' : '#e2e8f0',
              color: step === 1 ? '#fff' : theme.textMuted,
              fontSize: '0.875rem',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            1. Lesebestätigung
          </div>
          <div
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: step === 2 ? '#0284c7' : isDark ? '#334155' : '#e2e8f0',
              color: step === 2 ? '#fff' : theme.textMuted,
              fontSize: '0.875rem',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            2. Digitale Signatur (Passwort)
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
          {error && (
            <div
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                border: '1px solid #ef4444',
                color: isDark ? '#fca5a5' : '#b91c1c',
                borderRadius: '6px',
                fontSize: '0.875rem',
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {step === 1 ? (
            <div>
              <div
                style={{
                  backgroundColor: theme.innerBg,
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: `1px solid ${theme.border}`,
                }}
              >
                <div style={{ fontWeight: 600, color: theme.textMain, marginBottom: '8px' }}>
                  Dokumenten-Information:
                </div>
                <div style={{ fontSize: '0.875rem', color: theme.textSub, lineHeight: 1.5 }}>
                  {document.description || 'Keine Kurzbeschreibung hinterlegt.'}
                </div>
                <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '0.8rem', color: theme.textMuted, flexWrap: 'wrap' }}>
                  <span>📅 Gültig ab: {document.valid_from}</span>
                  {document.valid_until && <span>⏳ Gültig bis: {document.valid_until}</span>}
                  <span>📎 Datei: {document.file_name}</span>
                </div>
              </div>

              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <a
                  href={`/api/dms/documents/${document.id}/download?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                  onClick={(e) => handleDownloadDocument(e, document.id)}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    backgroundColor: '#0284c7',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: '6px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    boxShadow: '0 2px 4px rgba(2, 132, 199, 0.25)',
                  }}
                >
                  📄 Dokument jetzt im neuen Tab öffnen / lesen
                </a>
              </div>

              <div
                style={{
                  backgroundColor: isDark ? 'rgba(56, 189, 248, 0.08)' : '#f0f9ff',
                  border: isDark ? '1px solid rgba(56, 189, 248, 0.25)' : '1px solid #bae6fd',
                  padding: '16px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <input
                  type="checkbox"
                  id="confirm-step1"
                  checked={step1Checked}
                  onChange={(e) => setStep1Checked(e.target.checked)}
                  style={{ marginTop: '4px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label
                  htmlFor="confirm-step1"
                  style={{ cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1.5, color: theme.textMain }}
                >
                  <strong>Schritt 1 von 2:</strong> Ich bestätige hiermit, dass ich das Dokument{' '}
                  <span style={{ color: theme.accent, fontWeight: 600 }}>{document.title}</span> (Version {document.version})
                  vollständig zur Kenntnis genommen, gelesen und inhaltlich verstanden habe.
                </label>
              </div>

              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '9px 18px',
                    backgroundColor: isDark ? '#334155' : '#f1f5f9',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    color: theme.textSub,
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  disabled={!step1Checked}
                  onClick={() => setStep(2)}
                  style={{
                    padding: '9px 24px',
                    backgroundColor: step1Checked ? '#0284c7' : isDark ? '#334155' : '#e2e8f0',
                    border: 'none',
                    borderRadius: '6px',
                    color: step1Checked ? '#fff' : theme.textMuted,
                    cursor: step1Checked ? 'pointer' : 'not-allowed',
                    fontWeight: 600,
                  }}
                >
                  Weiter zu Schritt 2 →
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleFinalSubmit}>
              <div
                style={{
                  backgroundColor: isDark ? 'rgba(234, 179, 8, 0.1)' : '#fefce8',
                  border: isDark ? '1px solid rgba(234, 179, 8, 0.3)' : '1px solid #fde047',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  color: isDark ? '#fef08a' : '#854d0e',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                }}
              >
                ⚖️ <strong>Rechtsverbindliche Kenntnisnahme & Digitale Unterschrift:</strong>
                <br />
                Zur endgültigen Abzeichnung dieser Weisung/Dienstanweisung ist die Eingabe Ihres persönlichen
                Benutzerkennworts erforderlich. Mit Bestätigung wird diese Unterweisung mit Ihrem Namen,
                Zeitstempel und IP-Adresse unveränderlich dokumentiert.
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: theme.textSub,
                  }}
                >
                  Ihr Anmeldekennwort:
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort eingeben..."
                  autoFocus
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    backgroundColor: theme.inputBg,
                    border: `2px solid ${theme.inputBorder}`,
                    borderRadius: '6px',
                    color: theme.textMain,
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{
                    padding: '9px 18px',
                    backgroundColor: isDark ? '#334155' : '#f1f5f9',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    color: theme.textSub,
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  ← Zurück
                </button>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      padding: '9px 18px',
                      backgroundColor: isDark ? '#334155' : '#f1f5f9',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '6px',
                      color: theme.textSub,
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !password}
                    style={{
                      padding: '10px 24px',
                      backgroundColor: loading || !password ? (isDark ? '#334155' : '#cbd5e1') : '#16a34a',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      cursor: loading || !password ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: !loading && password ? '0 2px 6px rgba(22, 163, 74, 0.3)' : 'none',
                    }}
                  >
                    {loading ? 'Wird signiert...' : '✍️ Verbindlich abzeichnen'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
